/**
 * Tenant Initial Setup Dialog (Modal Wizard)
 *
 * A modal wizard for provisioning a new tenant database.
 * Steps:
 *   1. Confirm corporate ID
 *   2. Enter initial admin user info (password auto-generated by default)
 *   3. Execute DB provisioning + show credentials
 *
 * This dialog blocks all other interactions and confirms cancellation.
 *
 * @module pages/platform/tenants/TenantSetupDialog
 */

import {
  AlertCircle,
  Check,
  ChevronRight,
  ClipboardCopy,
  Database,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  RefreshCw,
  Rocket,
  UserPlus,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api');

/** Email service availability flag. Set to true when email sending is implemented. */
const EMAIL_SERVICE_AVAILABLE = false;

type Step = 1 | 2 | 3;

/** Password generation configuration */
const PASSWORD_CONFIG = {
  length: 16,
  uppercase: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lowercase: 'abcdefghjkmnpqrstuvwxyz',
  digits: '23456789',
  symbols: '!@#$%&*',
} as const;

/**
 * Generate a cryptographically random password.
 * Uses crypto.getRandomValues for secure randomness.
 */
function generateSecurePassword(): string {
  const { uppercase, lowercase, digits, symbols, length } = PASSWORD_CONFIG;
  const allChars = uppercase + lowercase + digits + symbols;

  // Ensure at least one of each required character type
  const required = [
    uppercase[crypto.getRandomValues(new Uint32Array(1))[0] % uppercase.length],
    lowercase[crypto.getRandomValues(new Uint32Array(1))[0] % lowercase.length],
    digits[crypto.getRandomValues(new Uint32Array(1))[0] % digits.length],
    symbols[crypto.getRandomValues(new Uint32Array(1))[0] % symbols.length],
  ];

  // Fill rest with random characters
  const remaining = length - required.length;
  const randomValues = crypto.getRandomValues(new Uint32Array(remaining));
  const rest = Array.from(randomValues, (v) => allChars[v % allChars.length]);

  // Shuffle all characters
  const combined = [...required, ...rest];
  const shuffleValues = crypto.getRandomValues(new Uint32Array(combined.length));
  combined.sort((_, __, i = 0) => (shuffleValues[i++] ?? 0) - 0.5);

  // Fisher-Yates shuffle for proper randomization
  for (let i = combined.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [combined[i], combined[j]] = [combined[j], combined[i]];
  }

  return combined.join('');
}

/** Props for TenantSetupDialog */
interface TenantSetupDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Corporate number (13-digit) */
  corporateId: string;
  /** Corporation name */
  corporateName: string;
  /** Callback on successful provisioning */
  onSuccess: (dbName: string) => void;
}

/** Admin user info collected in Step 2 */
interface AdminInfo {
  email: string;
  name: string;
  passwordMode: 'auto' | 'manual';
  generatedPassword: string;
  manualPassword: string;
  confirmPassword: string;
}

/**
 * Modal wizard for tenant DB provisioning.
 *
 * Prevents background interaction and confirms cancellation
 * at any step during the process.
 */
export default function TenantSetupDialog({
  open,
  onOpenChange,
  corporateId,
  corporateName,
  onSuccess,
}: TenantSetupDialogProps) {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const [adminInfo, setAdminInfo] = useState<AdminInfo>({
    email: '',
    name: '',
    passwordMode: 'auto',
    generatedPassword: generateSecurePassword(),
    manualPassword: '',
    confirmPassword: '',
  });

  const [provisionResult, setProvisionResult] = useState<{
    success: boolean;
    dbName?: string;
    password?: string;
  } | null>(null);

  const steps = [
    { id: 1, label: t('tenant.wizard.steps.confirm'), icon: Database },
    { id: 2, label: t('tenant.wizard.steps.admin'), icon: UserPlus },
    { id: 3, label: t('tenant.wizard.steps.provision'), icon: Rocket },
  ];

  /** Get the effective password based on mode */
  const effectivePassword = useMemo(
    () => (adminInfo.passwordMode === 'auto' ? adminInfo.generatedPassword : adminInfo.manualPassword),
    [adminInfo.passwordMode, adminInfo.generatedPassword, adminInfo.manualPassword],
  );

  /** Regenerate auto password */
  const handleRegeneratePassword = useCallback(() => {
    setAdminInfo((prev) => ({ ...prev, generatedPassword: generateSecurePassword() }));
  }, []);

  /** Copy password to clipboard */
  const handleCopyPassword = useCallback(async (password: string) => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  /** Validate admin info fields */
  const validateAdminInfo = useCallback((): string | null => {
    if (!adminInfo.email || !adminInfo.email.includes('@')) {
      return t('tenant.wizard.admin.errors.invalidEmail');
    }
    if (!adminInfo.name.trim()) {
      return t('tenant.wizard.admin.errors.nameRequired');
    }
    if (adminInfo.passwordMode === 'manual') {
      if (adminInfo.manualPassword.length < 12) {
        return t('tenant.wizard.admin.errors.passwordTooShort');
      }
      if (adminInfo.manualPassword !== adminInfo.confirmPassword) {
        return t('tenant.wizard.admin.errors.passwordMismatch');
      }
      // Check complexity
      const hasUpper = /[A-Z]/.test(adminInfo.manualPassword);
      const hasLower = /[a-z]/.test(adminInfo.manualPassword);
      const hasDigit = /\d/.test(adminInfo.manualPassword);
      const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(adminInfo.manualPassword);
      if (!hasUpper || !hasLower || !hasDigit || !hasSymbol) {
        return t('tenant.wizard.admin.errors.passwordWeak');
      }
    }
    return null;
  }, [adminInfo, t]);

  /** Handle cancel with confirmation */
  const handleCancel = useCallback(() => {
    if (provisionResult?.success) {
      onOpenChange(false);
      return;
    }

    const hasInput = adminInfo.email || adminInfo.name || adminInfo.manualPassword || currentStep > 1;

    if (hasInput) {
      const confirmed = window.confirm(t('tenant.wizard.cancelConfirm'));
      if (!confirmed) return;
    }

    setCurrentStep(1);
    setError(null);
    setAdminInfo({
      email: '',
      name: '',
      passwordMode: 'auto',
      generatedPassword: generateSecurePassword(),
      manualPassword: '',
      confirmPassword: '',
    });
    setProvisionResult(null);
    setShowPassword(false);
    setCopied(false);
    onOpenChange(false);
  }, [adminInfo, currentStep, provisionResult, onOpenChange, t]);

  /** Execute DB provisioning */
  const handleProvision = async () => {
    try {
      setLoading(true);
      setError(null);

      const password = effectivePassword;

      const res = await fetch(`${API_BASE}/tenants/${corporateId}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminUser: {
            email: adminInfo.email,
            name: adminInfo.name,
            initialPassword: password,
          },
        }),
      });

      const json = (await res.json()) as { error?: string; dbName?: string };
      if (!res.ok) throw new Error(json.error || t('tenant.wizard.provision.errorGeneric'));

      setProvisionResult({ success: true, dbName: json.dbName, password });
      onSuccess(json.dbName || '');
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(t('tenant.wizard.provision.errorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleCancel();
        }
      }}
    >
      <DialogContent
        className="max-w-2xl bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleCancel();
        }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{t('tenant.wizard.title')}</DialogTitle>
          <DialogDescription>{t('tenant.wizard.description', { name: corporateName })}</DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between relative px-4 py-2">
          <div className="absolute top-1/2 left-8 right-8 h-0.5 bg-gray-200 -translate-y-1/2" />
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id || provisionResult?.success;

            let ringColor = 'bg-white border-gray-200 text-gray-400';
            if (isActive && !provisionResult?.success) {
              ringColor = 'bg-blue-600 border-blue-200 text-white shadow-md';
            } else if (isCompleted) {
              ringColor = 'bg-green-500 border-green-200 text-white';
            }

            return (
              <div key={step.id} className="relative z-10 flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${ringColor}`}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                </div>
                <span
                  className={`text-[10px] mt-1.5 font-bold transition-colors ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mx-1">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common.errors.unknown')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Confirm Corporate ID */}
        {currentStep === 1 && (
          <div className="space-y-4 px-1">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t('tenant.wizard.confirm.corporateId')}</span>
                <span className="font-mono font-bold text-gray-900">{corporateId}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t('tenant.wizard.confirm.corporateName')}</span>
                <span className="font-bold text-gray-900">{corporateName}</span>
              </div>
            </div>
            <p className="text-sm text-gray-500">{t('tenant.wizard.confirm.note')}</p>

            <div className="flex justify-end pt-2">
              <Button onClick={() => setCurrentStep(2)} className="gap-2">
                {t('common.next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Admin User Info */}
        {currentStep === 2 && (
          <div className="space-y-4 px-1">
            <p className="text-sm text-gray-600">{t('tenant.wizard.admin.description')}</p>
            <div className="grid gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="admin-email">{t('tenant.wizard.admin.email')}</Label>
                <Input
                  id="admin-email"
                  type="email"
                  className="bg-white border-gray-300"
                  placeholder={t('tenant.wizard.admin.emailPlaceholder')}
                  value={adminInfo.email}
                  onChange={(e) => setAdminInfo({ ...adminInfo, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-name">{t('tenant.wizard.admin.name')}</Label>
                <Input
                  id="admin-name"
                  className="bg-white border-gray-300"
                  placeholder={t('tenant.wizard.admin.namePlaceholder')}
                  value={adminInfo.name}
                  onChange={(e) => setAdminInfo({ ...adminInfo, name: e.target.value })}
                />
              </div>

              {/* Password Mode Selection */}
              <div className="space-y-3">
                <Label>{t('tenant.wizard.admin.passwordLabel')}</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={adminInfo.passwordMode === 'auto' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAdminInfo({ ...adminInfo, passwordMode: 'auto' })}
                    className="flex-1"
                  >
                    {t('tenant.wizard.admin.passwordAuto')}
                  </Button>
                  <Button
                    type="button"
                    variant={adminInfo.passwordMode === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAdminInfo({ ...adminInfo, passwordMode: 'manual' })}
                    className="flex-1"
                  >
                    {t('tenant.wizard.admin.passwordManual')}
                  </Button>
                </div>

                {adminInfo.passwordMode === 'auto' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <Input
                          readOnly
                          type={showPassword ? 'text' : 'password'}
                          className="bg-gray-50 border-gray-300 font-mono text-sm pr-10"
                          value={adminInfo.generatedPassword}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleRegeneratePassword}
                        title={t('tenant.wizard.admin.regenerate')}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleCopyPassword(adminInfo.generatedPassword)}
                        title={t('tenant.wizard.admin.copyPassword')}
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <ClipboardCopy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">{t('tenant.wizard.admin.autoPasswordNote')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Input
                        id="admin-password"
                        type="password"
                        className="bg-white border-gray-300"
                        placeholder={t('tenant.wizard.admin.passwordPlaceholder')}
                        value={adminInfo.manualPassword}
                        onChange={(e) => setAdminInfo({ ...adminInfo, manualPassword: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="admin-password-confirm">{t('tenant.wizard.admin.confirmPassword')}</Label>
                      <Input
                        id="admin-password-confirm"
                        type="password"
                        className="bg-white border-gray-300"
                        placeholder={t('tenant.wizard.admin.confirmPasswordPlaceholder')}
                        value={adminInfo.confirmPassword}
                        onChange={(e) => setAdminInfo({ ...adminInfo, confirmPassword: e.target.value })}
                      />
                    </div>
                    <p className="text-xs text-gray-500">{t('tenant.wizard.admin.manualPasswordNote')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                {t('common.back')}
              </Button>
              <Button
                onClick={() => {
                  const validationError = validateAdminInfo();
                  if (validationError) {
                    setError(validationError);
                    return;
                  }
                  setError(null);
                  setCurrentStep(3);
                }}
                className="gap-2"
              >
                {t('common.next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Provision */}
        {currentStep === 3 && (
          <div className="space-y-4 px-1">
            {provisionResult?.success ? (
              <div className="space-y-4">
                <div className="bg-green-50 text-green-700 p-4 rounded-lg text-center font-bold flex items-center justify-center gap-2 border border-green-200">
                  <Check className="w-5 h-5" />
                  {t('tenant.wizard.provision.success')}
                </div>
                {provisionResult.dbName && (
                  <div className="text-xs font-mono bg-gray-100 px-3 py-2 rounded">DB: {provisionResult.dbName}</div>
                )}

                {/* Credentials display after provisioning */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-blue-800">
                    {t('tenant.wizard.provision.credentialsTitle')}
                  </h4>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-600">{t('tenant.wizard.admin.email')}</span>
                      <span className="font-mono">{adminInfo.email}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-600">{t('tenant.wizard.admin.passwordLabel')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">
                          {showPassword ? provisionResult.password : '••••••••••••'}
                        </span>
                        <button
                          type="button"
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          type="button"
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleCopyPassword(provisionResult.password || '')}
                        >
                          {copied ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <ClipboardCopy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email send button (disabled when not available) */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 gap-2"
                    disabled={!EMAIL_SERVICE_AVAILABLE}
                    title={
                      EMAIL_SERVICE_AVAILABLE
                        ? t('tenant.wizard.provision.sendEmail')
                        : t('tenant.wizard.provision.emailNotAvailable')
                    }
                  >
                    <Mail className="w-4 h-4" />
                    {t('tenant.wizard.provision.sendEmail')}
                    {!EMAIL_SERVICE_AVAILABLE && (
                      <span className="text-[10px] text-gray-400 ml-1">
                        ({t('tenant.wizard.provision.emailNotAvailable')})
                      </span>
                    )}
                  </Button>
                </div>

                <Button
                  className="w-full"
                  onClick={() => {
                    setCurrentStep(1);
                    setProvisionResult(null);
                    setAdminInfo({
                      email: '',
                      name: '',
                      passwordMode: 'auto',
                      generatedPassword: generateSecurePassword(),
                      manualPassword: '',
                      confirmPassword: '',
                    });
                    setShowPassword(false);
                    setCopied(false);
                    onOpenChange(false);
                  }}
                >
                  {t('tenant.wizard.provision.close')}
                </Button>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-gray-700">{t('tenant.wizard.provision.summaryTitle')}</h4>
                  <div className="text-sm space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('tenant.wizard.confirm.corporateId')}</span>
                      <span className="font-mono font-bold">{corporateId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('tenant.wizard.confirm.corporateName')}</span>
                      <span className="font-bold">{corporateName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('tenant.wizard.admin.email')}</span>
                      <span className="font-mono">{adminInfo.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('tenant.wizard.admin.name')}</span>
                      <span>{adminInfo.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('tenant.wizard.admin.passwordLabel')}</span>
                      <span className="text-xs text-gray-400">
                        {adminInfo.passwordMode === 'auto'
                          ? t('tenant.wizard.admin.passwordAuto')
                          : t('tenant.wizard.admin.passwordManual')}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {t('tenant.wizard.provision.warning')}
                </p>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    {t('common.back')}
                  </Button>
                  <Button onClick={handleProvision} disabled={loading} className="gap-2 bg-blue-600 hover:bg-blue-700">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('tenant.wizard.provision.loading')}
                      </>
                    ) : (
                      <>
                        <Rocket className="w-4 h-4" />
                        {t('tenant.wizard.provision.execute')}
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
